/**
 * DeepSeek AI API Integration
 * Generates Amazon product reviews using AI
 */

const DEEPSEEK_API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY || 'sk-f0a56900628c49d3b6ea5d6e0b3378c8';
const DEEPSEEK_ENDPOINT = 'https://api.deepseek.com/v1/chat/completions';
const MODEL = 'deepseek-chat';

/**
 * Generate a review using DeepSeek AI
 * @param {number} stars - Number of stars (1-5)
 * @param {string} tone - Review tone (Enthousiaste/Romantique/Amusant/Détaillé)
 * @returns {Promise<string>} - Generated review text
 */
export async function generateReview(stars, tone) {
  // Build a natural, authentic prompt based on tone
  const toneInstructions = {
    'Enthousiaste': 'enthousiaste et positif',
    'Romantique': 'doux et romantique',
    'Amusant': 'amusant et léger',
    'Détaillé': 'réfléchi et spécifique'
  };

  const toneStyle = toneInstructions[tone] || 'sincère et honnête';

  const systemPrompt = `Tu écris un avis Amazon authentique en tant que personne réelle qui a acheté un jeu de tour pour couples pour son/sa partenaire. Écris comme si tu envoyais un message à un ami - décontracté, naturel, et sincère. Garde-le COURT (250-400 caractères max). Pas de langage marketing, juste des pensées authentiques. IMPORTANT: Écris en FRANÇAIS.`;

  const userPrompt = `Écris un avis ${stars} étoiles pour un jeu de tour pour couples. Sois ${toneStyle}. Écris TRÈS COURT - juste 2-4 phrases max (250-400 caractères au total). Sonne comme une vraie personne qui tape sur son téléphone, pas comme un critique professionnel. ${stars <= 3 ? 'Mentionne ce qui pourrait être amélioré.' : 'Partage ce que tu as vraiment aimé.'} N'utilise pas trop de points d'exclamation. Sois naturel et décontracté. ÉCRIS EN FRANÇAIS.`;

  try {
    const response = await fetch(DEEPSEEK_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: MODEL,
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userPrompt
          }
        ],
        temperature: 0.8,
        max_tokens: 150
      })
    });

    if (!response.ok) {
      throw new Error(`DeepSeek API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (data.choices && data.choices.length > 0) {
      return data.choices[0].message.content.trim();
    } else {
      throw new Error('No review generated');
    }
  } catch (error) {
    console.error('Error generating review:', error);
    throw new Error('Failed to generate review. Please try again.');
  }
}
